import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Field, OLIVE, errMsg, fmtDateTime } from "./dpa/dpaUtils";
import { MfaSetup, BackupCodes } from "./MfaSteps";

const CodeAction = ({ label, needsPassword, onSubmit, testId }) => {
  const [f, setF] = useState({ code: "", password: "" });
  return (
    <div className="grid md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
      <Field label="Current app code" value={f.code} onChange={(v) => setF({ ...f, code: v })} testId={`${testId}-code`} />
      {needsPassword ? <Field label="Password" type="password" value={f.password} onChange={(v) => setF({ ...f, password: v })} testId={`${testId}-password`} /> : <div />}
      <Button variant="outline" className="rounded-none" disabled={f.code.length !== 6 || (needsPassword && !f.password)} onClick={() => onSubmit(f)} data-testid={`${testId}-btn`}>{label}</Button>
    </div>
  );
};

export const MfaCard = () => {
  const [s, setS] = useState(null);
  const [mode, setMode] = useState(null);
  const [codes, setCodes] = useState(null);
  const load = () => api.get("/auth/mfa/status").then(({ data }) => setS(data));
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const regen = async ({ code }) => {
    try { setCodes((await api.post("/auth/mfa/me/backup-codes", { code })).data.backup_codes); setMode(null); }
    catch (e) { toast.error("Couldn't regenerate codes", { description: errMsg(e) }); }
  };
  const disable = async (f) => {
    try { await api.post("/auth/mfa/me/disable", f); toast.success("Two-step verification turned off"); setMode(null); load(); }
    catch (e) { toast.error("Couldn't turn off", { description: errMsg(e) }); }
  };
  return (
    <Card className="rounded-none p-6 space-y-4" data-testid="account-mfa-card">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Two-step verification (MFA)</div>
        {s.enabled
          ? <span className="text-xs font-bold text-emerald-700 flex items-center gap-1" data-testid="mfa-status-on"><ShieldCheck className="h-4 w-4" />On</span>
          : <span className="text-xs font-bold text-slate-500 flex items-center gap-1" data-testid="mfa-status-off"><ShieldOff className="h-4 w-4" />Off</span>}
      </div>
      {codes ? <BackupCodes codes={codes} doneLabel="Done" onDone={() => { setCodes(null); load(); }} />
        : mode === "setup" ? (
          <MfaSetup start={async () => (await api.post("/auth/mfa/me/start")).data}
            confirm={async (code) => (await api.post("/auth/mfa/me/confirm", { code })).data}
            onEnrolled={(d) => { toast.success("Two-step verification is on"); setMode(null); setCodes(d.backup_codes); }} onCancel={() => setMode(null)} />
        ) : s.enabled ? (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">Turned on {fmtDateTime(s.enrolled_at)} · <span data-testid="mfa-backup-left">{s.backup_codes_left}</span> backup codes left{s.required && " · required for your account"}</div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-none" onClick={() => setMode(mode === "regen" ? null : "regen")} data-testid="mfa-regen-toggle"><RefreshCw className="h-4 w-4 mr-1" />New backup codes</Button>
              {!s.required && <Button variant="outline" className="rounded-none text-red-700" onClick={() => setMode(mode === "disable" ? null : "disable")} data-testid="mfa-disable-toggle"><ShieldOff className="h-4 w-4 mr-1" />Turn off</Button>}
            </div>
            {mode === "regen" && <CodeAction label="Generate new codes" onSubmit={regen} testId="mfa-regen" />}
            {mode === "disable" && <CodeAction label="Turn off MFA" needsPassword onSubmit={disable} testId="mfa-disable" />}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Add a code from your phone at every sign-in, so a stolen password alone isn't enough.</p>
            <Button className="rounded-none text-white" style={OLIVE} onClick={() => setMode("setup")} data-testid="mfa-enable-btn"><ShieldCheck className="h-4 w-4 mr-1" />Turn on two-step verification</Button>
          </div>
        )}
    </Card>
  );
};
